import React from 'react';
import { Gauge } from './Gauge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { getScoreLevel } from '../data/services';
import { 
  ChevronRight, ExternalLink, CheckCircle2, 
  ArrowRight, Shield, Leaf 
} from 'lucide-react';

export const ResultsPage = ({ score, recommendations, leadData, onRestart }) => {
  const scoreLevel = getScoreLevel(score);

  return (
    <div className="animate-fade-in-up space-y-8" data-testid="results-page">
      {/* Welcome message */}
      {leadData && (
        <div className="text-center p-4 rounded-xl bg-[#006241]/10 border border-[#006241]/20">
          <p className="text-[#006241] font-medium">
            Merci {leadData.name.split(' ')[0]} ! Voici votre analyse personnalisée.
          </p>
        </div>
      )}

      {/* Score section */}
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 font-outfit mb-2">
          Votre niveau de dépendance GAFAM
        </h2>
        <p className="text-slate-500 mb-8">
          Basé sur vos réponses au questionnaire
        </p>
        
        <Gauge score={score} />
        
        <p className={`mt-6 text-lg ${scoreLevel.textColor} font-medium`}>
          {scoreLevel.message}
        </p>
      </div>

      {/* Recommendations section */}
      {recommendations.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-[#0d2a47]" />
            <h3 className="text-xl font-semibold text-[#0d2a47] font-outfit">
              Alternatives souveraines proposées par oueb.it
            </h3>
          </div>

          {recommendations.map((rec, index) => (
            <Card 
              key={index} 
              className="p-5 border-l-4 hover:shadow-md transition-shadow"
              style={{ borderLeftColor: '#0d2a47' }}
              data-testid={`recommendation-${index}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-[#0d2a47]">
                      {rec.category}
                    </span>
                  </div>
                  
                  <p className="text-sm text-slate-500 mb-3">
                    Vous utilisez : {rec.selected_services.join(', ')}
                  </p>

                  <div className="flex items-center gap-2 mb-3">
                    <ArrowRight className="w-4 h-4 text-[#006241]" />
                    <span className="font-semibold text-[#0d2a47]">
                      {rec.alternative.service}
                    </span>
                  </div>

                  <ul className="space-y-1">
                    {rec.alternative.benefits.slice(0, 3).map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-[#006241] mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={rec.alternative.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm font-medium text-[#006241] hover:underline whitespace-nowrap"
                >
                  En savoir plus
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* CTA section */}
      <Card className="p-6 bg-gradient-to-br from-[#0d2a47]/5 to-[#006241]/5 border-none">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#006241]/10 flex items-center justify-center flex-shrink-0">
            <Leaf className="w-6 h-6 text-[#006241]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0d2a47] font-outfit mb-2">
              Prêt à reprendre le contrôle de vos données ?
            </h3>
            <p className="text-slate-600 mb-4">
              Nos experts oueb.it vous accompagnent dans votre transition vers des solutions souveraines Infomaniak.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://oueb.it/#contact"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-6 py-2 bg-[#0d2a47] hover:bg-[#1a4a7a] text-white font-medium transition-colors"
                data-testid="contact-btn"
              >
                Nous contacter
                <ChevronRight className="w-4 h-4" />
              </a>
              <Button
                onClick={onRestart}
                variant="outline"
                className="rounded-full px-6 border-slate-300"
                data-testid="restart-btn"
              >
                Refaire le test
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* oueb.it branding */}
      <div className="text-center pt-4 border-t border-slate-100">
        <p className="text-sm text-slate-400">
          Ce questionnaire est proposé par{' '}
          <a 
            href="https://oueb.it" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#006241] hover:underline font-medium"
          >
            oueb.it
          </a>
          , partenaire Infomaniak
        </p>
      </div>
    </div>
  );
};

export default ResultsPage;
